import {
  errorCodeOf,
  sanitizeErrorParams,
  type ErrorParams,
  isServerFailureFault,
  referenceOf,
  userFacingErrorMessage as sharedUserFacingErrorMessage,
} from '@financialharmony/ui/errors';
import { extractFunctionError } from '@/utils/errorUtils';
import { CORRELATION_HEADER } from '@/clients/correlatedFetch';
import { errorSentence } from '@/i18n/errorSentence';

/**
 * supabase-js transport errors. Their own message ("Edge Function returned a non-2xx status
 * code", "Failed to send a request to the Edge Function") describes the transport, never what
 * went wrong for the person.
 */
const TRANSPORT_ERROR_NAMES = new Set(['FunctionsHttpError', 'FunctionsFetchError', 'FunctionsRelayError']);
const TRANSPORT_MESSAGES = new Set([
  'Edge Function returned a non-2xx status code',
  'Failed to send a request to the Edge Function',
  'Relay Error invoking the Edge Function',
]);

/** A ServiceError.errorType such as `REPOSITORY_ERROR`: a code, never a sentence. */
const ERROR_CODE_SHAPE = /^[A-Z][A-Z0-9_]*$/;

interface FailureFields {
  status?: number;
  errorCode?: string;
  /** The variable parts of the code's catalog sentence (`errorParams` in the body, FIN-1524). */
  errorParams?: ErrorParams;
  reference?: string;
}

/**
 * A failed edge-function call, carrying what `userFacingErrorMessage` needs to decide whether
 * its `message` may be shown: the HTTP status, the body's `error` code and the support reference.
 */
export class EdgeFunctionFailure extends Error {
  readonly status?: number;
  readonly errorCode?: string;
  readonly errorParams?: ErrorParams;
  readonly reference?: string;

  constructor(message: string, fields: FailureFields = {}) {
    super(message);
    this.name = 'EdgeFunctionFailure';
    this.status = fields.status;
    this.errorCode = fields.errorCode;
    this.errorParams = fields.errorParams;
    this.reference = fields.reference;
  }
}

interface FailureBody {
  error?: unknown;
  message?: unknown;
  errorParams?: unknown;
  reference?: unknown;
}

function failureFromBody(
  body: FailureBody | null,
  status: number | undefined,
  headerReference: string | null | undefined,
  fallback: string,
): EdgeFunctionFailure {
  const rawError = typeof body?.error === 'string' && body.error.trim() !== '' ? body.error : undefined;
  // handleError()'s body is `{ error: CODE, message }`; some older functions answer a 400 with
  // `{ success: false, error: "<sentence>" }` instead, so an `error` that is not code-shaped is the sentence.
  const errorCode = rawError && ERROR_CODE_SHAPE.test(rawError) ? rawError : undefined;
  const sentence = typeof body?.message === 'string' && body.message.trim() !== '' ? body.message : undefined;
  const message = sentence ?? (errorCode ? undefined : rawError) ?? fallback;
  const bodyReference = typeof body?.reference === 'string' ? body.reference : undefined;
  const reference = bodyReference ?? (headerReference ? headerReference.slice(0, 8) : undefined);
  return new EdgeFunctionFailure(message, { status, errorCode, errorParams: sanitizeErrorParams(body?.errorParams), reference });
}

/**
 * Turns whatever a `supabase.functions.invoke` call failed with into an Error that keeps the
 * status, error code and reference. A FunctionsHttpError's body can be read only once: a caller
 * that already parsed it passes it as `preParsedBody`.
 */
export async function toEdgeFunctionFailure(error: unknown, fallback: string, preParsedBody?: FailureBody | null): Promise<Error> {
  const name = (error as { name?: unknown } | null)?.name;
  if (name === 'FunctionsHttpError') {
    const context = (error as { context?: { status?: number; bodyUsed?: boolean; headers?: Headers } }).context;
    let body: FailureBody | null = null;
    if (preParsedBody !== undefined) body = preParsedBody;
    else if (!context?.bodyUsed) body = await extractFunctionError(error);
    return failureFromBody(body, context?.status, context?.headers?.get?.(CORRELATION_HEADER), fallback);
  }
  if (error instanceof Error) return error;
  return new EdgeFunctionFailure(fallback);
}

/** The same as `toEdgeFunctionFailure`, for a client that calls `fetch` directly. */
export async function failureFromResponse(response: Response, fallback: string): Promise<EdgeFunctionFailure> {
  const body: FailureBody | null = await response.json().catch(() => null);
  return failureFromBody(body, response.status, response.headers.get(CORRELATION_HEADER), fallback);
}

function statusOf(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : undefined;
}

/** `errorCode` (EdgeFunctionFailure), `errorType` (InvoiceGenerationApiError) or `error` (CodedError). */
function codeOf(error: unknown): string | undefined {
  const coded = error as { errorType?: unknown; error?: unknown } | null;
  const code = errorCodeOf(error) ?? coded?.errorType ?? coded?.error;
  return typeof code === 'string' ? code : undefined;
}

function isTransportError(error: unknown): boolean {
  const { name, message } = (error ?? {}) as { name?: unknown; message?: unknown };
  if (typeof name === 'string' && TRANSPORT_ERROR_NAMES.has(name)) return true;
  // The same sentence after a client re-wrapped it in a plain Error.
  return typeof message === 'string' && TRANSPORT_MESSAGES.has(message);
}

/**
 * The text to put in front of a person when something failed in the consultant app (FIN-1272).
 *
 * The same rule apps/client applies through `@financialharmony/ui`'s helper of the same name: a
 * refusal's own sentence (a 4xx ServiceError) is shown, while a server fault (a 5xx other than the
 * curated 501–504, or a REPOSITORY_ERROR/INTERNAL_ERROR code) shows the caller's `fallback`, with
 * the support reference when the server sent one. On top of that rule, a supabase-js transport
 * error and a non-Error value always show the fallback.
 */
export function userFacingErrorMessage(error: unknown, fallback: string): string {
  const reference = referenceOf(error);
  const withReference = (message: string): string => (reference ? `${message} Reference: ${reference}` : message);

  if (isTransportError(error)) return withReference(fallback);
  if (isServerFailureFault({ status: statusOf(error), errorCode: codeOf(error) })) return withReference(fallback);
  return sharedUserFacingErrorMessage(error, fallback, errorSentence);
}

/** `userFacingErrorMessage` for a raw `functions.invoke` error, reading its body first. */
export async function userFacingFailureText(error: unknown, fallback: string): Promise<string> {
  return userFacingErrorMessage(await toEdgeFunctionFailure(error, fallback), fallback);
}
